import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as moment from 'moment-timezone';
import { DialogCambioComponent } from '../../dialog-cambio/dialog-cambio.component';
import { TransactionUserDTO } from 'src/app/dashboard/interfaces/transaction-user.dto';

@Component({
  selector: 'app-transaction-card',
  templateUrl: './transaction-card.component.html',
  styleUrls: ['./transaction-card.component.sass']
})
export class TransactionCardComponent implements OnInit {
  @Input() transaccion: TransactionUserDTO | undefined;
  @Input() expanded: boolean = false;
  @Output() toggleExpand: EventEmitter<void> = new EventEmitter();
  constructor(public dialog: MatDialog) {
  }

  public beneficiario: string | null = ''
  public status: string = ''

  ngOnInit() {
    this.status = this.transaccion!.estadoActual;
    if (this.transaccion?.estadoActual === 'POR_PROCESAR_CBPAY' || this.transaccion?.estadoActual === 'POR_PROCESAR_CRX') {
      if (this.transaccion) {
        this.transaccion.estadoActual = 'POR_PROCESAR';
      }
    }
    if (this.transaccion?.motivo === 'FONDEO WALLET') {
      this.beneficiario = this.transaccion.pagador === null ? this.transaccion.remitente : this.transaccion.pagador;
      if (this.transaccion?.estadoActual === 'OPERACION EXITOSA') {
        this.status = 'ENVIADA'
      }
    } else {
      this.beneficiario = this.transaccion!.beneficiario;
    }
    if (this.transaccion?.motivo === 'AJUSTE DE BALANCE') {
      this.status = 'ENVIADA'
    }
    if (this.transaccion?.motivo === 'CRIPTOMONEDA' && this.transaccion?.beneficiario === null) {
      if (this.transaccion.walletTRC20) {
        this.beneficiario = "Transferencia a Wallet: " + this.transaccion.walletTRC20;
      } else {
        this.beneficiario = "Transferencia a Wallet"
      }
    }
    if (this.transaccion?.motivo === 'AC') {
      this.beneficiario = this.transaccion?.cuentaBeneficiario?.nombre + ' ' + this.transaccion.cuentaBeneficiario?.apellidos;
      if (this.transaccion.estadoActual !== 'ENVIADA' && this.transaccion.estadoActual !== 'POR PROCESAR') {
        this.transaccion.estadoActual = 'POR_PROCESAR';
      }
    }
    // La conversión de zona horaria se hace en el getter
    // `fechaCreacionChile` con moment-timezone (ver abajo). Antes acá se
    // restaban 3 horas a mano sobre `getHours()` (que ya es hora del
    // navegador), lo que producía una doble resta y mostraba la hora muy
    // atrasada.
  }

  /**
   * `fechaCreacion` llega como instante UTC (ISO con `Z`, ej.
   * `2026-07-09T21:42:13.000Z`). Lo mostramos convertido a hora de Chile
   * con moment-timezone, que respeta el horario de verano/invierno
   * (UTC-3 / UTC-4). No usamos el pipe `date` de Angular con
   * `'America/Santiago'` porque su parámetro de zona NO soporta nombres
   * IANA (cae a la zona del navegador) — solo acepta offsets fijos, que
   * romperían con el cambio de DST.
   */
  get fechaCreacionChile(): string {
    if (!this.transaccion?.fechaCreacion) return '';
    const m = moment.tz(this.transaccion.fechaCreacion, 'America/Santiago');
    return m.isValid() ? m.format('MMM D, YYYY, h:mm:ss A') : '';
  }

  openDialog() {
    this.transaccion!.beneficiario = this.beneficiario!;
    const isMobile = window.innerWidth < 768;
    this.dialog.open(DialogCambioComponent, {
      data: this.transaccion,
      panelClass: 'aguacate-dialog',
      maxWidth: isMobile ? '95vw' : '600px',
      minWidth: isMobile ? '90vw' : '520px',
      maxHeight: '90vh',
    });
  }

  get estadoActualLabel(): string | null {
    if (!this.transaccion?.estadoActual) return null;

    let estado = this.transaccion.estadoActual;
    if (estado === 'POR_PROCESAR_CBPAY' || estado === 'POR_PROCESAR_CRX') {
      estado = 'POR_PROCESAR';
    }
    return estado.replaceAll('_', ' ');
  }

  /**
   * Un envío CLP→VES rechazado devuelve la plata al saldo, y eso hay que
   * decirlo: el estado «RECHAZADA» solo, sin más, se lee como plata perdida.
   *
   * La fila duplicada que veía el cliente la colapsa
   * `TransactionsComponent.colapsarDevoluciones`; este aviso es lo que explica
   * la fila que queda.
   *
   * Es cierto para FIAT sin excepción: los cuatro caminos que dejan una
   * transferencia en RECHAZADA crean la reversión (`reject-transaction.ts:93`,
   * `accept-cbpay-transaction-service.ts:345`, `transaction.service.ts:2463` y
   * `:2710`); los únicos tipos que quedan afuera son AC, SWAP_USDT_VES y
   * RETIRO_VES, que no son este riel.
   *
   * El arreglo de fondo es exponer `b2.motivo` como columna propia en la
   * query. Cuando eso entre, este aviso sigue siendo verdadero.
   */
  get avisoDevolucion(): string | null {
    const donde = this.devolucionEn;
    if (donde === 'VES') {
      return 'El envío no se procesó. La devolución quedó en tu saldo en bolívares (VES).';
    }
    return donde === 'CLP' ? 'El envío no se procesó. El monto volvió completo a tu saldo.' : null;
  }

  /** La devolución del rechazo quedó en el saldo VES (chileno, sin abono CLP). */
  get devolucionEnVes(): boolean {
    return this.devolucionEn === 'VES';
  }

  /** Dónde quedó la devolución de un envío FIAT CLP rechazado; `null` si no aplica. */
  private get devolucionEn(): 'CLP' | 'VES' | null {
    const t = this.transaccion;
    if (!t) return null;
    const rechazada = (t.estadoActual || '')
      .toUpperCase()
      .replaceAll(' ', '_')
      .includes('RECHAZAD');
    const esFiatClp =
      t.tipoTransaccion === 'FIAT' && t.tipoMonedaOrigen === 'CLP';
    if (!rechazada || !esFiatClp) return null;
    // Desde DEVOLUCION_VES_ENVIOS_CL_DESDE, el envío CLP→VES de un chileno se
    // devuelve en bolívares a su saldo VES y no hay abono CLP: queda solo el
    // cargo (sin el par que marca `colapsarDevoluciones`).
    if (!(t as any).devolucionEnClp && (t.tipoMonedaDestino || '').toUpperCase() === 'VES') {
      return 'VES';
    }
    return 'CLP';
  }

  get tasaConversion(): number | null {
    const t = this.transaccion as any;
    if (!t) return null;
    const candidatos = [
      t.tn, t.tasa, t.tasaConversion, t.rate,
      t.precio_compra, t.precioCompra,
      t.valorCambio?.tasa, t.valorCambio?.tasaUsdtClp,
      t.tasaCambio?.tasa,
    ];
    for (const c of candidatos) {
      const n = +c;
      if (!isNaN(n) && n > 0) return n;
    }
    const pares: Array<[any, any]> = [
      [t.monedaOrigen, t.monedaDestino],
      [t.totalMonedaOrigen, t.totalMonedaDestino],
      [t.totalEnvio, t.totalDest],
    ];
    for (const [o, d] of pares) {
      const origen = Math.abs(+o);
      const destino = Math.abs(+d);
      if (origen && destino) {
        return origen >= destino ? origen / destino : destino / origen;
      }
    }
    const desc: string = t.comentarios || t.description || '';
    const m = desc.match(/(?:tasa|rate)[:\s]+([0-9]*\.?[0-9]+)/i);
    if (m) {
      const n = +m[1];
      if (!isNaN(n) && n > 0) return n < 1 ? 1 / n : n;
    }
    return null;
  }

  /**
   * Monto de USDT comprado en una conversión CLP→USDT.
   *
   * Sale del propio `motivo` (`Conversion CLP/USDT: 602.0289016721835 USDT @
   * 933.51 CLP/USDT`) y, para las filas viejas donde ese número quedó escrito
   * como "undefined" o el motivo es sólo `Conversion CLP/USDT`, del comentario
   * (`CLP 50000.00 convertido a USDT | Tasa: ... | Total: 55.94843792 USDT`).
   *
   * El patrón exige que el número esté pegado a "USDT" (sólo espacios en
   * medio), así que no confunde el "0.0011189 CLP/USDT" de la tasa ni el
   * "CLP 50000.00 convertido a USDT" del monto en pesos.
   */
  private get usdtConvertido(): number | null {
    const t = this.transaccion as any;
    for (const fuente of [t?.motivo, t?.comentarios]) {
      const m = (fuente || '').match(/([0-9]+\.?[0-9]*)\s*USDT/i);
      if (m) {
        const n = +m[1];
        if (!isNaN(n) && n > 0) return n;
      }
    }
    return null;
  }

  get motivoDisplay(): string {
    const motivo = this.transaccion?.motivo || '';
    // La conversión CLP→USDT graba un motivo técnico con la tasa y hasta 16
    // decimales. La tasa ya se muestra en su propia fila ("Tasa:"), así que
    // acá basta el monto comprado.
    if (motivo.startsWith('Conversion CLP/USDT')) {
      const usdt = this.usdtConvertido;
      if (usdt === null) return 'Compra de USDT con saldo CLP';
      const monto = usdt.toLocaleString('es-CL', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `Compra de ${monto} USDT`;
    }
    if (!motivo.toLowerCase().includes('undefined')) return motivo;
    const tasa = this.tasaConversion;
    if (tasa) {
      const formatted = tasa.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return motivo.replace(/undefined/gi, formatted);
    }
    return motivo.replace(/:\s*undefined\s*USDT/gi, '').replace(/\s*undefined\s*/gi, ' ').trim();
  }

  onToggle(): void {
    this.toggleExpand.emit();
  }

  getStatusBg(): string {
    const estado = (this.status || '').toUpperCase().replaceAll(' ', '_');
    if (estado.includes('ANULAD') || estado.includes('RECHAZAD')) return '#dc2626';
    if (estado.includes('POR_PROCESAR') || estado.includes('PROCESANDO')) return '#f59e0b';
    if (estado.includes('ESPERANDO')) return '#eab308';
    if (estado.includes('COMPLETAD') || estado.includes('ENVIAD') || estado.includes('EXITOSA')) return '#16a34a';
    return '#6b7280';
  }

  getStatusColor(): string {
    return '#ffffff';
  }

}
